import { useCallback, useContext, useMemo, useState } from 'react';
import { createBookingDraft, fetchBookingDrafts, updateBookingDraft } from '../api/BookingDraft';
import { uploadQuotation } from '../api/Quotation';
import { fetchQueryById, updateQuery } from '../api/Query';
import { normalizeQuotationRooms } from '../components/QuotationBuilder/quotationRoomOccupancy';
import { getDatePlusDays } from '../utils/date';
import { getQueryStatusValueForLane } from '../utils/queryStatus';
import { generatePdfBlob } from '../utils/pdf';
import { toast } from 'react-toastify';
import { BookingDraftCreateInput, BookingDraftRecord, ShareQuotationInput } from '../components/PackageBuilder/contracts';
import { CreateQueryRoom } from '../types/Query';
import { AuthContext } from '../context/AuthContext';

const toAmountPaise = (amount: number) => Math.max(0, Math.round(Number(amount || 0) * 100));
const FALLBACK_CUSTOMER_NAME = 'Customer';
const FALLBACK_PACKAGE_NAME = 'Package Quotation';
const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g;
const BOOKING_DETAILS_MARKER = 'bookingDetails=';

const sanitizePdfFilenamePart = (value: string | undefined, fallback: string) => {
  const normalized = String(value || '')
    .split('')
    .map((char) => (char.charCodeAt(0) < 32 ? ' ' : char))
    .join('')
    .replace(INVALID_FILENAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized || fallback;
};

const buildQuotationPdfFilename = ({
  clientName,
  packageName,
}: {
  clientName?: string;
  packageName?: string;
}) => {
  const safeClientName = sanitizePdfFilenamePart(clientName, FALLBACK_CUSTOMER_NAME);
  const safePackageName = sanitizePdfFilenamePart(packageName, FALLBACK_PACKAGE_NAME);
  return `${safeClientName}-${safePackageName}.pdf`;
};

const buildQueryRoomsPayload = (hotelRooms: ShareQuotationInput['hotelRooms']): CreateQueryRoom[] => (
  normalizeQuotationRooms(hotelRooms).map((room) => ({
    Adults: Math.max(1, Number(room.adults || 0)),
    Children: Math.max(0, Number(room.children || 0)),
    ChildrenAges: Array.isArray(room.childrenAges)
      ? room.childrenAges
        .slice(0, Math.max(0, Number(room.children || 0)))
        .map((age) => Number(age ?? 0))
      : [],
  }))
);

const getQueryPax = (hotelRooms: ShareQuotationInput['hotelRooms']) => (
  normalizeQuotationRooms(hotelRooms).reduce(
    (sum, room) => sum + Math.max(1, Number(room.adults || 0)) + Math.max(0, Number(room.children || 0)),
    0
  )
);

const upsertPackageLine = (description: string, packageName: string) => {
  const trimmedPackageName = packageName.trim();
  if (!trimmedPackageName) return description.trim();

  const cleanedLines = description
    .split('\n')
    .map((line) => line.trimEnd())
    .filter((line) => !/^package\s*:/i.test(line.trim()));

  const baseDescription = cleanedLines.join('\n').trim();
  return baseDescription
    ? `${baseDescription}\nPackage: ${trimmedPackageName}`
    : `Package: ${trimmedPackageName}`;
};

const updateStructuredBookingDescription = ({
  description,
  packageName,
  rooms,
  pax,
}: {
  description: string;
  packageName: string;
  rooms: CreateQueryRoom[];
  pax: number;
}) => {
  const markerIndex = description.indexOf(BOOKING_DETAILS_MARKER);
  if (markerIndex === -1) return null;

  const prefix = description.slice(0, markerIndex);
  const tail = description.slice(markerIndex + BOOKING_DETAILS_MARKER.length);
  const firstBrace = tail.indexOf('{');
  const lastBrace = tail.lastIndexOf('}');
  if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) return null;

  const between = tail.slice(0, firstBrace);
  const jsonText = tail.slice(firstBrace, lastBrace + 1);
  const suffix = tail.slice(lastBrace + 1);

  try {
    const parsed = JSON.parse(jsonText);
    const adults = rooms.reduce((sum, room) => sum + Number(room.Adults || 0), 0);
    const children = rooms.reduce((sum, room) => sum + Number(room.Children || 0), 0);
    const nextDescription = {
      ...parsed,
      ...(packageName.trim() ? { packageName: packageName.trim() } : {}),
      passengers: {
        ...(parsed?.passengers || {}),
        adults,
        children,
        total: pax,
        rooms: rooms.map((room) => ({
          adults: Number(room.Adults || 0),
          children: Number(room.Children || 0),
          childrenAges: Array.isArray(room.ChildrenAges) ? room.ChildrenAges : [],
        })),
      },
    };

    return `${prefix}${BOOKING_DETAILS_MARKER}${between}${JSON.stringify(nextDescription)}${suffix}`;
  } catch {
    return null;
  }
};

const buildUpdatedQueryDescription = ({
  currentDescription,
  packageName,
  rooms,
  pax,
}: {
  currentDescription?: string;
  packageName: string;
  rooms: CreateQueryRoom[];
  pax: number;
}) => {
  const description = String(currentDescription || '');
  const structured = updateStructuredBookingDescription({
    description,
    packageName,
    rooms,
    pax,
  });

  if (structured) return structured;
  return upsertPackageLine(description, packageName);
};

const createDraftPayload = ({
  queryId,
  title,
  currency,
  providerRef,
  totalPrice,
  travelStartDate,
  travelEndDate,
  customerId,
  assignedId,
  createdBy,
}: {
  queryId: number;
  title: string;
  currency: 'INR' | 'USD';
  providerRef: Record<string, unknown>;
  totalPrice: number;
  travelStartDate: string;
  travelEndDate: string;
  customerId?: number;
  assignedId?: number;
  createdBy?: number;
}): BookingDraftCreateInput => ({
  productType: 'package',
  provider: 'CRM',
  title: title || 'Package Quotation',
  currency,
  amountPaise: toAmountPaise(totalPrice),
  startAt: travelStartDate || null,
  endAt: travelEndDate || null,
  providerRef,
  queryId,
  customerId,
  assignedId,
  createdBy,
});

export const useShareQuotation = ({
  nightsCount,
  queryId,
  startDate,
  hotelRooms,
  totalPrice,
  providerRef,
  pdfRef,
  currency,
  title,
  customerId,
  assignedId,
  createdBy,
  download = false,
  shareDraft = true,
  client,
}: ShareQuotationInput) => {
  const [isSharing, setIsSharing] = useState(false);
  const authContext = useContext(AuthContext) as { user?: { id?: number | string } } | undefined;
  const actingUserId = authContext?.user?.id ? Number(authContext.user.id) : undefined;

  const canShare = useMemo(
    () => Boolean((queryId || download) && startDate && nightsCount >= 0 && Number(totalPrice || 0) >= 0 && !isSharing),
    [download, isSharing, nightsCount, queryId, startDate, totalPrice]
  );

  const share = useCallback(async () => {
    if (!queryId && !download) return;

    setIsSharing(true);
    try {
      const travelStartDate = startDate;
      const travelEndDate = getDatePlusDays(startDate, nightsCount);

      const sourceEl = pdfRef.current;
      const pdfFilename = buildQuotationPdfFilename({
        clientName: client?.name,
        packageName: title,
      });
      const pdfPromise: Promise<Blob | null> = (async () => {
        if (!sourceEl) return null;
        try {
          const blob = await generatePdfBlob(sourceEl, pdfFilename);
          return blob;
        } catch {
          return null;
        }
      })();

      if (shareDraft && queryId) {
        try {
          const all = await fetchBookingDrafts(queryId);
          const existing = (all || []).find((draft: BookingDraftRecord) =>
            Number(draft.query_id || draft.queryId || 0) === Number(queryId)
          );

          if (existing) {
            await updateBookingDraft(existing.id, {
              providerRef,
              startAt: travelStartDate || null,
              endAt: travelEndDate || null,
              amountPaise: toAmountPaise(totalPrice),
              currency,
              title: title || undefined,
            });
            toast.success('Booking draft updated');
          } else {
            const payload = createDraftPayload({
              queryId,
              title,
              currency,
              providerRef,
              totalPrice,
              travelStartDate,
              travelEndDate,
              customerId,
              assignedId: actingUserId ?? assignedId,
              createdBy: actingUserId ?? createdBy,
            });
            await createBookingDraft(payload);
            toast.success('Booking draft created');
          }
        } catch {
          try {
            const payload = createDraftPayload({
              queryId,
              title,
              currency,
              providerRef,
              totalPrice,
              travelStartDate,
              travelEndDate,
              customerId,
              assignedId: actingUserId ?? assignedId,
              createdBy: actingUserId ?? createdBy,
            });
            await createBookingDraft(payload);
            toast.success('Booking draft created');
          } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create booking draft';
            toast.error(message);
          }
        }
      }

      try {
        const pdfBlob = await pdfPromise;
        if (!pdfBlob) return;

        if (download) {
          try {
            const url = URL.createObjectURL(pdfBlob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = pdfFilename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);
          } catch {
            // best-effort download
          }

          if (!shareDraft || !queryId) return;
        }

        const formData = new FormData();
        formData.append('type', 'PDF');
        formData.append('amount', String(Number(totalPrice || 0)));
        formData.append('notes', `Quotation shared for query #${queryId}`);
        formData.append('query_id', String(queryId));
        formData.append('queryId', String(queryId));
        const file = new File([pdfBlob], pdfFilename, { type: 'application/pdf' });
        formData.append('file', file);

        await uploadQuotation(formData);
        if (shareDraft && queryId) {
          try {
            const rooms = buildQueryRoomsPayload(hotelRooms);
            const pax = getQueryPax(hotelRooms);
            let currentDescription = '';

            try {
              const query = await fetchQueryById(queryId);
              currentDescription = String(query?.description || '');
            } catch {
              // best-effort fetch; we can still update the key query fields below
            }

            await updateQuery(queryId, {
              status: getQueryStatusValueForLane('QUOTATION SHARED'),
              query_date: travelStartDate,
              ...(actingUserId ? { assigned_to: actingUserId } : {}),
              rooms,
              description: buildUpdatedQueryDescription({
                currentDescription,
                packageName: title || '',
                rooms,
                pax,
              }),
            });
          } catch (error: unknown) {
            const message = error instanceof Error
              ? error.message
              : 'Quotation uploaded but failed to update query';
            toast.error(message);
          }
        }
        toast.success('Quotation PDF uploaded');
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to upload quotation PDF';
        toast.error(message);
      }
    } finally {
      setIsSharing(false);
    }
  }, [
    assignedId,
    createdBy,
    currency,
    client,
    customerId,
    download,
    hotelRooms,
    shareDraft,
    actingUserId,
    nightsCount,
    pdfRef,
    providerRef,
    queryId,
    startDate,
    title,
    totalPrice,
  ]);

  return {
    canShare,
    isSharing,
    share,
  };
};
